/** Standard report packs: on-demand runs, history, downloads and email schedules. */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";

export type ReportDefinition = Schemas["ReportDefinitionOut"];
export type ReportRun = Schemas["ReportRunOut"];
export type ReportSchedule = Schemas["ReportScheduleOut"];
export type ReportScheduleIn = Schemas["ReportScheduleIn"];
export type ReportParamsIn = Schemas["ReportParamsIn"];
export type DatasetDefinition = Schemas["DatasetOut"];
export type CustomReport = Schemas["CustomReportOut"];
export type CustomReportIn = Schemas["CustomReportIn"];
export type CustomSectionSpec = Schemas["CustomSectionSpec"];

/** Run statuses that will still change; the history polls while any is present. */
export const PENDING_RUN_STATUSES = new Set(["queued", "running"]);

/** How often the history refreshes while a run is still generating. */
export const RUN_POLL_MS = 3000;

function invalidateReports(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ["reports"] });
}

/** Report packs the caller may generate. */
export function useReportCatalogue(): UseQueryResult<ReportDefinition[]> {
  return useQuery({
    queryKey: ["reports", "catalogue"],
    queryFn: async () => unwrap(await api.GET("/api/v1/reporting/library/reports")),
  });
}

/** The 50 latest runs visible to the caller, polling while one is still generating. */
export function useReportRuns(): UseQueryResult<ReportRun[]> {
  return useQuery({
    queryKey: ["reports", "runs"],
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/reporting/library/runs", { params: { query: { limit: 50 } } }))
        .items,
    refetchInterval: (query) =>
      query.state.data?.some((run) => PENDING_RUN_STATUSES.has(run.status)) ? RUN_POLL_MS : false,
  });
}

/** Queue an on-demand run of a report pack. */
export function useRequestRun(): UseMutationResult<
  ReportRun,
  Error,
  { key: string; params: ReportParamsIn }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, params }) =>
      unwrap(
        await api.POST("/api/v1/reporting/library/reports/{key}/runs", {
          params: { path: { key } },
          body: { params },
        }),
      ),
    onSuccess: () => {
      invalidateReports(qc);
    },
  });
}

/** Download a run's workbook through the API (tenant + CSRF middleware, re-authorised). */
export async function downloadRun(run: ReportRun): Promise<void> {
  const blob = unwrap(
    await api.GET("/api/v1/reporting/library/runs/{run_id}/download", {
      params: { path: { run_id: run.id } },
      parseAs: "blob",
    }),
  );
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = run.filename || "report.xlsx";
  document.body.append(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
    URL.revokeObjectURL(url);
  }
}

/** Schedules the caller owns or receives. */
export function useReportSchedules(enabled = true): UseQueryResult<ReportSchedule[]> {
  return useQuery({
    queryKey: ["reports", "schedules"],
    queryFn: async () => unwrap(await api.GET("/api/v1/reporting/library/schedules")),
    enabled,
  });
}

/** Create a schedule, or replace one when `id` is given. */
export function useSaveSchedule(): UseMutationResult<
  ReportSchedule,
  Error,
  { id?: string; body: ReportScheduleIn }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      id
        ? unwrap(
            await api.PUT("/api/v1/reporting/library/schedules/{schedule_id}", {
              params: { path: { schedule_id: id } },
              body,
            }),
          )
        : unwrap(await api.POST("/api/v1/reporting/library/schedules", { body })),
    onSuccess: () => {
      invalidateReports(qc);
    },
  });
}

/** Actions available on a schedule row. */
export type ScheduleAction = "pause" | "resume" | "run" | "delete";

/** Pause, resume, run now or delete a schedule. */
export function useScheduleAction(): UseMutationResult<
  unknown,
  Error,
  { id: string; action: ScheduleAction }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action }) => {
      const params = { path: { schedule_id: id } };
      switch (action) {
        case "delete":
          return unwrap(
            await api.DELETE("/api/v1/reporting/library/schedules/{schedule_id}", { params }),
          );
        case "pause":
          return unwrap(
            await api.POST("/api/v1/reporting/library/schedules/{schedule_id}/pause", { params }),
          );
        case "resume":
          return unwrap(
            await api.POST("/api/v1/reporting/library/schedules/{schedule_id}/resume", { params }),
          );
        case "run":
          return unwrap(
            await api.POST("/api/v1/reporting/library/schedules/{schedule_id}/run", { params }),
          );
      }
    },
    onSuccess: () => {
      invalidateReports(qc);
    },
  });
}

/** Datasets available in the custom report builder. */
export function useReportDatasets(enabled = true): UseQueryResult<DatasetDefinition[]> {
  return useQuery({
    queryKey: ["reports", "datasets"],
    queryFn: async () => unwrap(await api.GET("/api/v1/reporting/library/datasets")),
    enabled,
  });
}

/** Saved and role-seeded custom reports visible to the caller. */
export function useCustomReports(enabled = true): UseQueryResult<CustomReport[]> {
  return useQuery({
    queryKey: ["reports", "custom"],
    queryFn: async () => unwrap(await api.GET("/api/v1/reporting/library/custom-reports")),
    enabled,
  });
}

/** Create or update a custom report template. */
export function useSaveCustomReport(): UseMutationResult<
  CustomReport,
  Error,
  { id?: string; body: CustomReportIn }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      id
        ? unwrap(
            await api.PUT("/api/v1/reporting/library/custom-reports/{report_id}", {
              params: { path: { report_id: id } },
              body,
            }),
          )
        : unwrap(await api.POST("/api/v1/reporting/library/custom-reports", { body })),
    onSuccess: () => {
      invalidateReports(qc);
    },
  });
}

/** Delete a custom report template. */
export function useDeleteCustomReport(): UseMutationResult<unknown, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.DELETE("/api/v1/reporting/library/custom-reports/{report_id}", {
          params: { path: { report_id: id } },
        }),
      ),
    onSuccess: () => {
      invalidateReports(qc);
    },
  });
}
